import { useEffect, useRef } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { useCheckoutStatus } from '../../hooks/checkout/useCheckoutStatus'

import Icon from '../../components/ui/Icon'
import Loading from '../../components/loading/Loading'
import { Button } from '../../components/buttons/Button'
import { Buttons } from '../../components/buttons/Buttons'
import { ConfirmItemRow } from '../../components/confirm/confirmItemRow/ConfirmItemRow'

import { useApp } from '../../contexts/AppContext'
import { useAuth } from '../../contexts/AuthContext'
import { useConfig } from '../../contexts/ConfigContext'
import { RoleType } from '../../utils/jwtUtils'
import { Checkout as CheckoutModel, CheckoutStatus, CheckoutType } from '../../types/booking/booking'
import { ConfirmAsideVariant, ConfirmLayout, ItemCard } from '../../types/confirm/confirm'
import { formatMinutes, formatMoney, formatPoints, timeOf } from '../../utils/booking/booking'
import { toDisplayOnlyDate } from '../../utils/dateUtils'

const formatCountdown = (milliseconds: number) => {
  const totalSeconds = Math.ceil(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/** Same action button sizing as the booking confirmation. */
const ACTION_CLASS = 'min-w-0 flex-grow-0 flex-shrink-0 flex-sm-fill w-sm-auto'

type Tone = 'info' | 'success' | 'warning' | 'danger'

type StatusView = {
  tone: Tone
  icon: string
  title: string
  text: string
}

/** Same rows as the booking confirmation: one per session (or the pass being bought), with its price or "Pass". */
const checkoutItems = (checkout: CheckoutModel, t: TFunction, language: string): ItemCard[] => {
  if (checkout.type === CheckoutType.PASS_PURCHASE) {
    return [
      {
        id: 'pass',
        title: checkout.passProductName ?? t('booking.checkout.pass-purchase'),
        subtitle: t('booking.checkout.pass-purchase'),
        aside: formatMoney(
          checkout.amount && { ...checkout.amount, amount: checkout.amount.amount + (checkout.pointsAmount ?? 0) }
        ),
        asideVariant: ConfirmAsideVariant.PAID,
      },
    ]
  }

  return checkout.items.map(item => {
    const prepaid = item.prepaidMinutes >= item.durationMinutes
    return {
      id: `${item.courtId}-${item.startTime}`,
      title: item.courtName,
      subtitle: t('booking.booking-courts.summary-panel.slot-details', {
        date: toDisplayOnlyDate(item.startTime, language),
        time: `${timeOf(item.startTime)}–${timeOf(item.endTime)}`,
        durMin: formatMinutes(item.durationMinutes),
      }),
      aside: prepaid ? t('booking.booking-courts.summary-panel.plan') : formatMoney(item.price),
      asideVariant: prepaid ? ConfirmAsideVariant.INCLUDED : ConfirmAsideVariant.PAID,
    }
  })
}

/**
 * Checkout (payment hold) page. Recovers the hold from its id in the URL, so refreshing or opening it in another tab
 * shows the same hold and the same deadline; it never creates a hold. Confirmed details appear only after the server
 * confirmed the booking.
 */
const Checkout = () => {
  const { t, i18n } = useTranslation(['bookings', 'exceptions'])
  const navigate = useNavigate()
  const { authInfo } = useAuth()
  const { roles } = useApp()
  const [params] = useSearchParams()
  const checkoutId = params.get('checkoutId')
  const canceledAtProvider = params.get('canceled') === 'true'
  const { checkout, remainingMs, error, refresh, cancel, canceling } = useCheckoutStatus(
    checkoutId,
    !canceledAtProvider
  )

  // A checkout a manager made for a customer: the manager sees the on-behalf texts, the customer their own.
  const isManagement = roles.includes(RoleType.MANAGER) || roles.includes(RoleType.ADMIN)
  const managerView = isManagement && !!checkout?.madeOnBehalf

  const bookAgainPath = managerView ? '/m/booking' : authInfo ? '/booking' : '/guest-booking'

  // A confirmed payment may have bought a pass (or a top-up), which can extend the booking window: reload the config.
  const { refreshConfig } = useConfig()
  const configReloaded = useRef(false)
  const confirmed = checkout?.status === CheckoutStatus.CONFIRMED
  useEffect(() => {
    if (confirmed && authInfo && !managerView && !configReloaded.current) {
      configReloaded.current = true
      void refreshConfig()
    }
  }, [confirmed, authInfo, managerView, refreshConfig])

  if (!checkoutId) {
    return (
      <div className="ph-page confirm-page checkout-page">
        <div className="summary-card confirm-card p-3 p-sm-4">
          <p>{t('booking.checkout.missing')}</p>
          <Link to={bookAgainPath}>{t('booking.checkout.book-again')}</Link>
        </div>
      </div>
    )
  }

  if (!checkout) {
    return error ? (
      <div className="ph-page confirm-page checkout-page">
        <div className="summary-card confirm-card p-3 p-sm-4">
          <p>{error}</p>
          <Button label={t('booking.checkout.retry')} variant="ghost" onClick={refresh} />
        </div>
      </div>
    ) : (
      <Loading />
    )
  }

  const deadlinePassed = remainingMs !== null && remainingMs <= 0
  const amount = formatMoney(checkout.amount)
  const isBooking = checkout.type !== CheckoutType.PASS_PURCHASE
  const refundVoided = checkout.refund?.voided

  const statusView = (): StatusView => {
    switch (checkout.status) {
      case CheckoutStatus.PENDING_PAYMENT:
        return deadlinePassed
          ? {
              tone: 'warning',
              icon: 'clock',
              title: t('booking.checkout.status.deadline-passed.title'),
              text: t('booking.checkout.status.deadline-passed.text'),
            }
          : {
              tone: 'info',
              icon: 'clock',
              title: t('booking.checkout.status.pending.title'),
              text: canceledAtProvider
                ? t('booking.checkout.status.pending.text-returned')
                : t('booking.checkout.status.pending.text'),
            }
      case CheckoutStatus.RECONCILING:
        return {
          tone: 'info',
          icon: 'clock',
          title: t('booking.checkout.status.reconciling.title'),
          text: t('booking.checkout.status.reconciling.text'),
        }
      case CheckoutStatus.CONFIRMED:
        return {
          tone: 'success',
          icon: 'check',
          title: isBooking
            ? t('booking.checkout.status.confirmed.title')
            : t('booking.checkout.status.confirmed.title-pass'),
          text: !isBooking
            ? t('booking.checkout.status.confirmed.text-pass')
            : managerView
              ? t('booking.checkout.status.confirmed.text-on-behalf')
              : checkout.paymentRequired
                ? t('booking.checkout.status.confirmed.text')
                : t('booking.checkout.status.confirmed.text-no-payment'),
        }
      case CheckoutStatus.EXPIRED:
        return {
          tone: 'warning',
          icon: 'clock',
          title: t('booking.checkout.status.expired.title'),
          text: t('booking.checkout.status.expired.text'),
        }
      case CheckoutStatus.PAYMENT_FAILED:
        return {
          tone: 'danger',
          icon: 'x',
          title: t('booking.checkout.status.failed.title'),
          text: t('booking.checkout.status.failed.text'),
        }
      case CheckoutStatus.REFUND_PENDING:
        return {
          tone: 'warning',
          icon: 'warn',
          title: t('booking.checkout.status.refunding.title'),
          text: t('booking.checkout.status.refunding.text', { amount }),
        }
      case CheckoutStatus.REFUNDED:
        return {
          tone: 'warning',
          icon: 'info',
          title: t('booking.checkout.status.refunded.title'),
          text: refundVoided
            ? t('booking.checkout.status.refunded.text-voided', { amount })
            : t('booking.checkout.status.refunded.text', { amount }),
        }
      case CheckoutStatus.CANCELED:
      default:
        return {
          tone: 'info',
          icon: 'x',
          title: t('booking.checkout.status.canceled.title'),
          text: t('booking.checkout.status.canceled.text'),
        }
    }
  }

  const view = statusView()
  const items = checkoutItems(checkout, t, i18n.language)
  const canPay =
    checkout.status === CheckoutStatus.PENDING_PAYMENT && !deadlinePassed && Boolean(checkout.paymentUrl)
  const canCancel = Boolean(authInfo) && checkout.status === CheckoutStatus.PENDING_PAYMENT && !deadlinePassed
  const ended = ![CheckoutStatus.PENDING_PAYMENT, CheckoutStatus.RECONCILING, CheckoutStatus.CONFIRMED].includes(
    checkout.status
  )

  const pending = checkout.status === CheckoutStatus.PENDING_PAYMENT

  return (
    <div className="ph-page confirm-page checkout-page">
      <div className={`summary-card confirm-card confirm-card--summary p-3 p-sm-4`}>
        <div className="mb-8">
          <div className="eyebrow">
            {checkout.status === CheckoutStatus.CONFIRMED
              ? t('booking.checkout.booked')
              : t('booking.checkout.eyebrow')}
          </div>
          <h3 className="summary-title">
            {checkout.status === CheckoutStatus.CONFIRMED
              ? t('booking.checkout.title-confirmed')
              : managerView
                ? t('booking.checkout.title-on-behalf')
                : t('booking.checkout.title')}
          </h3>
          <p className="confirm-description">
            {!isBooking
              ? t('booking.checkout.sub-pass')
              : managerView
                ? t('booking.checkout.sub-on-behalf')
                : checkout.status === CheckoutStatus.CONFIRMED
                  ? t('booking.checkout.sub-confirmed')
                  : t('booking.checkout.sub')}
          </p>
        </div>

        {items.map(item => (
          <ConfirmItemRow
            key={item.id}
            item={item}
            layout={ConfirmLayout.SUMMARY}
            localValues={{}}
            onDetailChange={() => undefined}
          />
        ))}

        {(checkout.pointsAmount ?? 0) > 0 && (
          <div className="summary-stats ph-col mt-16">
            <div className="ph-row ph-between">
              <span className="muted">{t('booking.booking-courts.summary-panel.refund-points')}</span>
              <span className="mono text-success">
                {t('booking.booking-courts.summary-panel.refund-points-value', {
                  points: formatPoints(checkout.pointsAmount),
                })}
              </span>
            </div>
          </div>
        )}

        {checkout.amount && (
          <div className="sum-total">
            <div>
              <div className="eyebrow">
                {pending ? t('booking.checkout.amount-due') : t('booking.booking-courts.summary-panel.total')}
              </div>
            </div>
            <div className="value">{amount}</div>
          </div>
        )}

        <div className="summary-actions ph-col mt-16">
          {/* Where the confirmation shows the terms: the hold's countdown and the payment status. */}
          <div className={`checkout-status checkout-status--${view.tone}`} role="status" aria-live="polite">
            {pending && remainingMs !== null ? (
              <div className={`checkout-countdown ${deadlinePassed ? 'expired' : ''}`}>
                <span className="eyebrow">{t('booking.checkout.time-left')}</span>
                <span className="mono">{formatCountdown(remainingMs)}</span>
              </div>
            ) : (
              <Icon name={view.icon} size={22} />
            )}
            <div className="checkout-status-text">
              <strong>{view.title}</strong>
              <p>{view.text}</p>
              {pending && !deadlinePassed && <p className="tiny">{t('booking.checkout.temporary-notice')}</p>}
            </div>
          </div>

          <Buttons className="confirm-actions-buttons flex-sm-row justify-content-sm-end">
            {canCancel && (
              <Button
                label={t('booking.checkout.cancel')}
                variant="ghost"
                size="md"
                block
                className={ACTION_CLASS}
                loading={canceling}
                onClick={cancel}
              />
            )}
            {canPay && (
              <Button
                label={t('booking.checkout.pay-now', { amount })}
                icon={<Icon name="arrowR" size={16} />}
                iconPosition="right"
                size="md"
                block
                className={ACTION_CLASS}
                onClick={() => {
                  window.location.href = checkout.paymentUrl!
                }}
              />
            )}
            {checkout.status === CheckoutStatus.CONFIRMED && authInfo && (
              <Button
                label={
                  !isBooking
                    ? t('booking.checkout.my-passes')
                    : managerView
                      ? t('booking.checkout.all-bookings')
                      : t('booking.checkout.my-bookings')
                }
                icon={<Icon name="arrowR" size={16} />}
                iconPosition="right"
                size="md"
                block
                className={ACTION_CLASS}
                onClick={() => navigate(!isBooking ? '/passes' : managerView ? '/m/all' : '/history')}
              />
            )}
            {ended && (
              <Button
                label={t('booking.checkout.book-again')}
                icon={<Icon name="arrowR" size={16} />}
                iconPosition="right"
                size="md"
                block
                className={ACTION_CLASS}
                onClick={() => navigate(isBooking ? bookAgainPath : '/passes')}
              />
            )}
          </Buttons>
        </div>
      </div>
    </div>
  )
}

export default Checkout
